import React, { useState, useEffect } from "react"
import { Link } from "gatsby"
import ReactTooltip from "react-tooltip"

import FeatureIcon from "./featureicon"

/** Circle links to other feature pages, with counts (same idea as platform icons on topic pages). */
const FeatureIcons = ({
  featureNodes,
  currentFeatureSlug,
  communities,
  wrapperClassName = "mt-2",
  topicSlug,
}) => {
  const [showCount, setShowCount] = useState(8)

  useEffect(() => {
    if (typeof window !== "undefined" && window.screen.width < 600) {
      setShowCount(6)
    }
  }, [])

  const withCounts = (featureNodes || [])
    .map((f) => ({
      ...f,
      count: (communities || []).filter((c) => c[f.slug]).length,
    }))
    .filter((f) => f.count >= 1)
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))

  const featuresToShow = withCounts.filter((_, i) => i < showCount)
  const featuresNotShown = withCounts.filter((f) => !featuresToShow.includes(f))

  return (
    <section aria-labelledby="feature-nav-icons">
      <div className={wrapperClassName}>
        <h3 id="feature-nav-icons" className="sr-only">
          Browse other community features
        </h3>
        <ReactTooltip
          id="features-nav-tooltip"
          place="top"
          effect="solid"
          backgroundColor="black"
          textColor="white"
          className=""
        />
        <div className="flex flex-wrap">
          {featuresToShow.map((f) => (
            <Link
              to={
                topicSlug
                  ? `/topics/${topicSlug}/feature/${f.slug}/`
                  : `/features/${f.slug}/`
              }
              key={f.slug}
              className={`p-1 border-2 ${
                currentFeatureSlug === f.slug
                  ? "border-rose-500"
                  : "border-gray-100 hover:border-gray-200"
              } bg-white rounded-full relative cursor-pointer mr-3 mb-2`}
              data-for="features-nav-tooltip"
              data-tip={`${f.name || f.slug} (${f.count.toLocaleString()})`}
            >
              <div className="bg-white h-6 w-6 rounded-full overflow-hidden flex items-center justify-center">
                <FeatureIcon
                  feature={f.slug}
                  className="block h-5 w-5 text-gray-800 [&>svg]:h-full [&>svg]:w-full"
                />
              </div>
              <div
                className={`h-5 w-5 p-0.5 absolute -right-2 -bottom-2 rounded-full text-center text-xs ${
                  currentFeatureSlug === f.slug
                    ? "font-semibold text-gray-500 bg-gray-100"
                    : "text-gray-400 bg-gray-50"
                }`}
              >
                <div>{formatFeatureNavCount(f.count)}</div>
              </div>
            </Link>
          ))}

          {featuresNotShown.length ? (
            <Link
              to="/features/"
              className="p-1 border-2 border-gray-100 bg-white rounded-full relative cursor-pointer hover:border-gray-200 mr-3 mb-2 bg-gray-50"
              data-for="features-nav-tooltip"
              data-tip={`All ${withCounts.length} features`}
            >
              <div className="bg-white h-6 w-6 rounded-full overflow-hidden flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-6 text-gray-500"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 6v12m6-6H6"
                  />
                </svg>
              </div>
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  )
}

/** Badge text: full number under 1k; otherwise short k / M (tooltip carries exact count). */
function formatFeatureNavCount(n) {
  if (n < 1000) {
    return String(n)
  }
  if (n < 1_000_000) {
    return `${Math.round(n / 1000)}k`
  }
  return `${Math.round(n / 1_000_000)}M`
}

export default FeatureIcons
